/**
 * Copyright (c) 2025 eBUCIS, LLC. All rights reserved.
 *
 * NewConversationModal - Modal for starting a new conversation
 * Supports multi-select with pills for coaches messaging multiple students
 */

import { useState, useEffect } from 'react'
import { X, User, Loader2, Search, MessageSquare } from 'lucide-react'
import messagingService from '../../services/messagingService'
import coachService from '../../services/coachService'
import toast from 'react-hot-toast'
import { createLogger } from '../../utils/logger'

const logger = createLogger('NewConversationModal')

const NewConversationModal = ({
  isOpen,
  onClose,
  currentUserId,
  userRole, // 'coach', 'student', or 'guardian'
  children = [], // For guardians - list of their children
  onConversationCreated
}) => {
  const [recipients, setRecipients] = useState([])
  const [loading, setLoading] = useState(false)
  const [creating, setCreating] = useState(false)
  const [selectedRecipients, setSelectedRecipients] = useState([]) // Changed to array for multi-select
  const [selectedChild, setSelectedChild] = useState(null) // For guardians
  const [subject, setSubject] = useState('')
  const [initialMessage, setInitialMessage] = useState('')
  const [searchQuery, setSearchQuery] = useState('')
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)

  // Load available recipients based on user role
  useEffect(() => {
    if (!isOpen) return

    const loadRecipients = async () => {
      try {
        setLoading(true)

        if (userRole === 'coach') {
          // Load clients for coach - only those with messaging enabled
          // Note: getClients() returns client user data spread at top level (not nested under .client)
          const response = await coachService.getClients(false)
          if (response.success) {
            setRecipients(
              response.clients
                ?.filter((c) => c.permissions?.canSendMessages !== false)
                ?.map((c) => ({
                  ...c,
                  type: 'student'
                })) || []
            )
          }
        } else {
          // Load available coaches for student/guardian (single select only)
          const response = await messagingService.getAvailableCoaches(
            userRole === 'guardian' ? selectedChild?.id : null
          )
          if (response.success) {
            setRecipients(response.coaches || [])
          }
        }
      } catch (error) {
        logger.error('Error loading recipients:', error)
        toast.error('Failed to load contacts')
      } finally {
        setLoading(false)
      }
    }

    loadRecipients()
  }, [isOpen, userRole, selectedChild])

  const getRecipientName = (recipient) => {
    if (recipient.firstName || recipient.lastName) {
      return `${recipient.firstName || ''} ${recipient.lastName || ''}`.trim()
    }
    return recipient.email || 'Unknown'
  }

  const toggleRecipient = (recipient) => {
    // For students/guardians, only allow single selection (they can only message one coach)
    if (userRole !== 'coach') {
      setSelectedRecipients([recipient])
      return
    }

    // For coaches, allow multi-select
    const isSelected = selectedRecipients.some((r) => r.id === recipient.id)
    if (isSelected) {
      setSelectedRecipients(selectedRecipients.filter((r) => r.id !== recipient.id))
    } else {
      setSelectedRecipients([...selectedRecipients, recipient])
    }
  }

  const removeRecipient = (recipientId) => {
    setSelectedRecipients(selectedRecipients.filter((r) => r.id !== recipientId))
  }

  const handleCreate = async () => {
    if (selectedRecipients.length === 0) {
      toast.error('Please select at least one recipient')
      return
    }

    if (!initialMessage.trim()) {
      toast.error('Please enter a message')
      return
    }

    try {
      setCreating(true)

      // Create conversations for each selected recipient
      const results = []
      for (const recipient of selectedRecipients) {
        let conversationData

        if (userRole === 'coach') {
          // Coach starting conversation
          conversationData = {
            coachId: currentUserId,
            participantId: recipient.id,
            participantType: 'student',
            subject: subject.trim() || null,
            initialMessage: initialMessage.trim()
          }
        } else if (userRole === 'guardian') {
          // Guardian starting conversation about child
          conversationData = {
            coachId: recipient.id,
            regardingChildId: selectedChild?.id,
            subject: subject.trim() || null,
            initialMessage: initialMessage.trim()
          }
        } else {
          // Student starting conversation
          conversationData = {
            coachId: recipient.id,
            subject: subject.trim() || null,
            initialMessage: initialMessage.trim()
          }
        }

        const response = await messagingService.startConversation(conversationData)
        if (response.success) {
          results.push(response.conversation)
        }
      }

      if (results.length > 0) {
        toast.success(
          results.length === 1 ? 'Conversation started' : `${results.length} conversations started`
        )
        // Pass the first conversation (or last) to the callback
        onConversationCreated?.(results[results.length - 1])
        handleClose()
      }
    } catch (error) {
      logger.error('Error creating conversation:', error)
      toast.error(error.response?.data?.message || 'Failed to start conversation')
    } finally {
      setCreating(false)
    }
  }

  const handleClose = () => {
    setSelectedRecipients([])
    setSelectedChild(null)
    setSubject('')
    setInitialMessage('')
    setSearchQuery('')
    setIsDropdownOpen(false)
    onClose()
  }

  const filteredRecipients = recipients.filter((r) => {
    if (!searchQuery) return true
    const name = `${r.firstName || ''} ${r.lastName || ''}`.toLowerCase()
    const email = (r.email || '').toLowerCase()
    const query = searchQuery.toLowerCase()
    return name.includes(query) || email.includes(query)
  })

  // Filter out already selected recipients from the list
  const availableRecipients = filteredRecipients.filter(
    (r) => !selectedRecipients.some((s) => s.id === r.id)
  )

  if (!isOpen) return null

  const isMultiSelect = userRole === 'coach'

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-white dark:bg-gray-900 rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            New Conversation
          </h2>
          <button
            onClick={handleClose}
            className="p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-500"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {/* Child selector for guardians */}
          {userRole === 'guardian' && children.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Regarding child
              </label>
              <select
                value={selectedChild?.id || ''}
                onChange={(e) => {
                  const child = children.find((c) => c.id === e.target.value)
                  setSelectedChild(child)
                  setSelectedRecipients([]) // Reset recipients when child changes
                }}
                className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2"
              >
                <option value="">Select a child</option>
                {children.map((child) => (
                  <option key={child.id} value={child.id}>
                    {child.firstName} {child.lastName}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Recipient selection */}
          <div className="relative">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {userRole === 'coach' ? `Select student${isMultiSelect ? 's' : ''}` : 'Select coach'}
            </label>

            {/* Search input with pills inside */}
            <div
              className={`flex flex-wrap items-center gap-1.5 p-2 rounded-lg border bg-white dark:bg-gray-800 cursor-text ${
                isDropdownOpen
                  ? 'border-primary-500 ring-2 ring-primary-500'
                  : 'border-gray-300 dark:border-gray-600'
              }`}
              onClick={() => setIsDropdownOpen(true)}
            >
              {/* Selected recipients pills */}
              {selectedRecipients.map((recipient) => (
                <span
                  key={recipient.id}
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-sm
                    bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300"
                >
                  {getRecipientName(recipient)}
                  <button
                    onClick={(e) => {
                      e.stopPropagation()
                      removeRecipient(recipient.id)
                    }}
                    className="p-0.5 rounded-full hover:bg-primary-200 dark:hover:bg-primary-800 transition-colors"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </span>
              ))}
              {/* Search input */}
              <div className="flex-1 flex items-center gap-1.5 min-w-[100px]">
                {selectedRecipients.length === 0 && (
                  <Search className="w-4 h-4 text-gray-400 flex-shrink-0" />
                )}
                <input
                  type="text"
                  placeholder={selectedRecipients.length > 0 ? 'Add more...' : 'Search...'}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onFocus={() => setIsDropdownOpen(true)}
                  className="flex-1 bg-transparent text-gray-900 dark:text-gray-100 text-sm outline-none placeholder-gray-400"
                />
              </div>
            </div>

            {/* Dropdown list - only shows when focused */}
            {isDropdownOpen && (
              <>
                {/* Backdrop to close dropdown */}
                <div className="fixed inset-0 z-10" onClick={() => setIsDropdownOpen(false)} />
                {/* Dropdown */}
                <div className="absolute z-20 left-0 right-0 mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                  {loading ? (
                    <div className="flex items-center justify-center py-6">
                      <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
                    </div>
                  ) : availableRecipients.length === 0 ? (
                    <div className="text-center py-4 text-gray-500 dark:text-gray-400 text-sm">
                      {userRole === 'guardian' && !selectedChild
                        ? 'Select a child first'
                        : searchQuery
                          ? 'No matching contacts'
                          : recipients.length === 0
                            ? 'No contacts available'
                            : 'All contacts selected'}
                    </div>
                  ) : (
                    <div className="py-1">
                      {availableRecipients.map((recipient) => (
                        <button
                          key={recipient.id}
                          onClick={() => {
                            toggleRecipient(recipient)
                            setSearchQuery('')
                            // Keep dropdown open for multi-select, close for single
                            if (!isMultiSelect) {
                              setIsDropdownOpen(false)
                            }
                          }}
                          className="w-full flex items-center gap-3 px-3 py-2 text-left hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors"
                        >
                          <div className="w-8 h-8 rounded-full bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center flex-shrink-0">
                            <User className="w-4 h-4 text-primary-600 dark:text-primary-400" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-gray-900 dark:text-gray-100 truncate">
                              {getRecipientName(recipient)}
                            </p>
                            {recipient.email && (recipient.firstName || recipient.lastName) && (
                              <p className="text-sm text-gray-500 dark:text-gray-400 truncate">
                                {recipient.email}
                              </p>
                            )}
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </>
            )}
          </div>

          {/* Subject (optional) */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Subject <span className="text-gray-400">(optional)</span>
            </label>
            <input
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="What's this about?"
              maxLength={200}
              className="w-full rounded-lg border border-gray-300 dark:border-gray-600
                bg-white dark:bg-gray-800 px-3 py-2 text-gray-900 dark:text-gray-100"
            />
          </div>

          {/* Initial message */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Message
            </label>
            <textarea
              value={initialMessage}
              onChange={(e) => setInitialMessage(e.target.value)}
              placeholder="Write your message..."
              rows={4}
              className="w-full rounded-lg border border-gray-300 dark:border-gray-600
                bg-white dark:bg-gray-800 px-3 py-2 text-gray-900 dark:text-gray-100 resize-none"
            />
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-4 border-t border-gray-200 dark:border-gray-700">
          <button onClick={handleClose} className="btn btn-secondary" disabled={creating}>
            Cancel
          </button>
          <button
            onClick={handleCreate}
            disabled={
              creating ||
              selectedRecipients.length === 0 ||
              !initialMessage.trim() ||
              (userRole === 'guardian' && !selectedChild)
            }
            className="btn btn-primary inline-flex items-center gap-2"
          >
            {creating ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Starting...
              </>
            ) : (
              <>
                <MessageSquare className="w-4 h-4" />
                {selectedRecipients.length > 1
                  ? `Start ${selectedRecipients.length} Conversations`
                  : 'Start Conversation'}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  )
}

export default NewConversationModal
